/* Copyright (c) 2019 - 2025. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import { API, Auth } from "aws-amplify";
import { Data } from "../../../types/MessageDialogParams";

const apiName = "ApsBackend";

/* The Version of the APS API we are communicating with. The APS API uses semantic versioning
   vMAJOR.MINOR.PATCH. Doorway sends the version is expected in the Accept header. This version
   does not need to be updated each time APS changes the PATCH or MINOR versions, but any change to the
   MAJOR version will need to be handled appropriately (either Doorway updates it's MAJOR version or
   else APS provides backwards compatibility guarantees for older MAJOR versions).
*/
const apiVersion = "v1.0.0";

export interface Build {
  buildId: string;
  appName: string;
  iconUrl: string;
  buildLabel: string;
  state: string;
  updated: number;
  os: string;
}

const getHeaders = async () => {
  try {
    await Auth.currentAuthenticatedUser();
    const session = await Auth.currentSession();
    const jwtToken = session.getIdToken().getJwtToken();
    const accept = `application/vnd.aps.appshield.verimatrixcloud.net;version=${apiVersion}`;
    return { Authorization: jwtToken, Accept: accept };
  } catch (e) {
    Auth.signOut();
  }
};

const getApplications = async (orgId?: string, subscriptionType?: string) => {
  const headers = await getHeaders();
  const forOrg = orgId ? `&customer=${orgId}` : "";
  const forSubscriptionType = subscriptionType
    ? `&subscriptionType=${subscriptionType}`
    : "";
  const result = await API.get(
    apiName,
    `/applications?includeIcons=true${forOrg}${forSubscriptionType}`,
    {
      headers,
    }
  ).catch(() => {
    return null;
  });
  return result;
};

const getBuildById = async (id: string) => {
  const headers = await getHeaders();
  const result = await API.get(apiName, `/builds/${id}`, {
    headers,
  });
  return result;
};

const executeProtection = async (build: Data) => {
  const headers = await getHeaders();
  const result = await API.patch(apiName, `/builds/${build.id}?cmd=protect`, {
    headers,
  });
  return result;
};

const abortProtection = async (build: Data) => {
  const headers = await getHeaders();
  const result = await API.patch(apiName, `/builds/${build.id}?cmd=cancel`, {
    headers,
  });
  return result;
};

const getBuildArtifactsInfo = async (buildId: string) => {
  const headers = await getHeaders();
  const result = await API.get(
    apiName,
    `/report/artifacts?buildId=${buildId}`,
    {
      headers,
    }
  );
  return result;
};

export {
  getApplications,
  getBuildArtifactsInfo,
  getBuildById,
  executeProtection,
  abortProtection,
};
